import { api } from '../api/client';

import { API_TAG_APPLICATIONS_URL } from '../utils';
import { normalizeId } from '../utils/ids';

/**
 * PRIVATE applications (docs/plans/tags.md §3.3, §3.8; built in keepr-api
 * T2b): a person's own private tag — or an item tag used outside its
 * collection family (ruling q2) — on an item or a collection. They live in a
 * per-person store (`tagApplications`), never on the item: they need only
 * READ access to what they tag, write no History row, and reach nobody else.
 *
 *   PUT    /api/tag-applications   apply, up to 100 at a time
 *   DELETE /api/tag-applications   take off, up to 100 at a time
 *
 * One application is `{ tagId, targetType: 'item' | 'collection', target_id }`,
 * sent as `{ applications: [...] }`. ⚑ The body's envelope is written to the
 * plan's contract before T2b's routes exist; T2b's SCHEMA.md is the word, and
 * this module is the one place to change if it differs.
 *
 * A DRY RUN (`{ dryRun: true }`, keepr-api — the tags rulings, b32) is the
 * real call up to its first write: every refusal it would make comes back
 * exactly as the real call answers it (409 with `failures: [{ index, code }]`,
 * 400s alike), else 200 `{ dryRun: true, applied | removed, unchanged }`, and
 * nothing is written. The selection bar's private Tags… checks every batch
 * this way before its commit, as its collection-tag path does through bulk.
 *
 * Its callers: the item's chips and "Tag…" (hooks/useItemTagWriter), the
 * item form after a save, the selection bar's Tags… (T4b), and a collection's
 * Tag… and a private tag's Put on collections… (b53). Hand applications of
 * collection tags are NOT here: they ride the ordinary item writes
 * (`tagIds` on POST/PUT, bulk `tagIdsAdd` / `tagIdsRemove`).
 */

export const TAG_APPLICATIONS_BATCH = 100;
export const TARGET_TYPES = Object.freeze(['item', 'collection']);

/** Clean application rows: known target types, ids present, duplicates dropped. */
export function toApplications(rows = []) {
	const seen = new Set();
	const out = [];
	(Array.isArray(rows) ? rows : []).forEach(row => {
		const tagId = normalizeId(row?.tagId);
		const targetType = row?.targetType;
		const target = normalizeId(row?.target_id ?? row?.targetId);
		if (!tagId || !target || !TARGET_TYPES.includes(targetType)) return;
		const key = `${tagId}:${targetType}:${target}`;
		if (seen.has(key)) return;
		seen.add(key);
		out.push({ tagId, targetType, target_id: target });
	});
	return out;
}

const chunks = list => {
	const out = [];
	for (let at = 0; at < list.length; at += TAG_APPLICATIONS_BATCH) out.push(list.slice(at, at + TAG_APPLICATIONS_BATCH));
	return out;
};

// One batch after another, never in parallel: a refusal stops the run, and
// the batches already sent stay applied — the caller says so (§3 Batch
// actions: "say when the promise is weaker than usual"). Each batch is all
// or nothing on the API (409 with `failures: [{ index, code }]`, `index`
// counting inside that batch), so a thrown error carries `failedBatch`:
// `{ number, offset, applications, done }` — which batch, where it starts in
// the cleaned list, what it held, and how many batches landed before it —
// so a caller can name the rows a refusal is about.
async function send(method, rows) {
	const applications = toApplications(rows);
	const results = [];
	const batches = chunks(applications);
	for (let number = 0; number < batches.length; number += 1) {
		const batch = batches[number];
		try {
			const { data } = method === 'put'
				? await api.put(API_TAG_APPLICATIONS_URL, { applications: batch }) // eslint-disable-line no-await-in-loop
				: await api.delete(API_TAG_APPLICATIONS_URL, { data: { applications: batch } }); // eslint-disable-line no-await-in-loop
			results.push(data || {});
		} catch (error) {
			if (error && typeof error === 'object') {
				error.failedBatch = { number, offset: number * TAG_APPLICATIONS_BATCH, applications: batch, done: results.length };
			}
			throw error;
		}
	}
	return { batches: results.length, sent: applications.length, results };
}

// The dry run, batch by batch: EVERY batch is asked (a refusal in one does
// not stop the others — nothing is written, so it costs nothing to know them
// all), and a 409's `failures` are re-keyed from the batch to the cleaned
// list (`index` + the batch's offset) with the application they name. Any
// other refusal (a 400, a 5xx, no connection) is thrown with `failedBatch`,
// as the real run throws.
// → `{ dryRun: true, batches, sent, applied, removed, unchanged, refusals:
//     [{ index, code, application }] }`.
async function check(method, rows) {
	const applications = toApplications(rows);
	const batches = chunks(applications);
	const out = { dryRun: true, batches: batches.length, sent: applications.length, applied: 0, removed: 0, unchanged: 0, refusals: [] };
	for (let number = 0; number < batches.length; number += 1) {
		const batch = batches[number];
		const offset = number * TAG_APPLICATIONS_BATCH;
		try {
			const { data } = method === 'put'
				? await api.put(API_TAG_APPLICATIONS_URL, { applications: batch, dryRun: true }) // eslint-disable-line no-await-in-loop
				: await api.delete(API_TAG_APPLICATIONS_URL, { data: { applications: batch, dryRun: true } }); // eslint-disable-line no-await-in-loop
			out.applied += Number(data?.applied) || 0;
			out.removed += Number(data?.removed) || 0;
			out.unchanged += Number(data?.unchanged) || 0;
		} catch (error) {
			const failures = error?.response?.status === 409 ? error.response.data?.failures : null;
			if (!Array.isArray(failures) || !failures.length) {
				if (error && typeof error === 'object') {
					error.failedBatch = { number, offset, applications: batch, done: 0 };
				}
				throw error;
			}
			failures.forEach(entry => {
				const at = Number(entry?.index);
				const application = Number.isInteger(at) ? batch[at] : null;
				if (application) out.refusals.push({ index: offset + at, code: entry.code || null, application });
			});
		}
	}
	return out;
}

/**
 * Apply private tags: `[{ tagId, targetType, target_id }]`, in batches of 100.
 * `{ dryRun: true }` checks every batch and writes nothing (see `check`).
 */
export const applyPrivateTags = (rows, { dryRun = false } = {}) => (dryRun ? check('put', rows) : send('put', rows));

/**
 * Take private tags off: `[{ tagId, targetType, target_id }]`, in batches of
 * 100. `{ dryRun: true }` checks every batch and writes nothing.
 */
export const removePrivateTags = (rows, { dryRun = false } = {}) => (dryRun ? check('delete', rows) : send('delete', rows));
